import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, fmtMoney, fmtDate, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { ChevronLeft, ArrowDownRight, ArrowUpRight, Banknote, Search, FileText, Receipt } from "lucide-react";
import { toast } from "sonner";

const SOURCE_ICON = {
    invoice_payment: ArrowDownRight,
    bill_payment: ArrowUpRight,
    expense: Receipt,
    bank_txn: Banknote,
};

const SOURCE_LABEL = {
    invoice_payment: "Invoice payment",
    bill_payment: "Bill payment",
    expense: "Expense",
    bank_txn: "Bank transaction",
};

export default function BankAccountLedger() {
    const { id } = useParams();
    const { currentOrg } = useAuth();
    const [data, setData] = useState(null);
    const [q, setQ] = useState("");

    const load = async () => {
        try {
            const { data } = await api.get(`/bank-accounts/${id}/ledger`);
            setData(data);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    useEffect(() => { if (currentOrg && id) load(); }, [currentOrg, id]);

    const filtered = useMemo(() => {
        if (!data?.entries) return [];
        if (!q.trim()) return data.entries;
        const s = q.toLowerCase();
        return data.entries.filter((e) => (
            (e.description || "").toLowerCase().includes(s) ||
            (e.counterparty || "").toLowerCase().includes(s) ||
            (e.reference || "").toLowerCase().includes(s) ||
            String(e.amount).includes(s)
        ));
    }, [data, q]);

    if (!data) return <div className="text-muted-foreground">Loading ledger…</div>;

    const currency = data.account?.currency || currentOrg?.currency || "KYD";

    return (
        <div>
            <div className="mb-4">
                <Link to="/app/banking" className="text-sm text-muted-foreground hover:text-secondary inline-flex items-center gap-1">
                    <ChevronLeft className="h-4 w-4" /> Back to Bank Accounts
                </Link>
            </div>

            <PageHeader
                title={data.account?.name || "Account ledger"}
                description={`${data.account?.institution ? data.account.institution + " · " : ""}${(data.account?.account_type || "").replace("_", " ")} · ${currency}`}
            />

            <div className="grid sm:grid-cols-4 gap-4 mb-6">
                <StatCard label="Opening balance" value={fmtMoney(data.opening_balance, currency)} testId="ledger-opening" />
                <StatCard label="Inflow" value={fmtMoney(data.totals.inflow, currency)} tone="in" testId="ledger-inflow" />
                <StatCard label="Outflow" value={fmtMoney(data.totals.outflow, currency)} tone="out" testId="ledger-outflow" />
                <StatCard label="Current balance" value={fmtMoney(data.closing_balance, currency)} highlight testId="ledger-closing" />
            </div>

            <div className="mb-4 flex items-center gap-3">
                <div className="relative flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search ledger…"
                        className="pl-9 rounded-xl border-sand-300 bg-white"
                        data-testid="ledger-search"
                    />
                </div>
                <div className="text-xs text-muted-foreground">
                    {filtered.length} of {data.entries.length} entries
                </div>
            </div>

            {filtered.length === 0 ? (
                <EmptyState icon={FileText} title={q ? "No matches" : "No activity yet"} description={q ? "Try a different search." : "Payments and transactions on this account will appear here."} />
            ) : (
                <div className="rounded-3xl bg-white border border-sand-200 shadow-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-sand-100 text-xs uppercase tracking-wider text-muted-foreground">
                                <tr>
                                    <th className="text-left px-5 py-3">Date</th>
                                    <th className="text-left px-5 py-3">Type</th>
                                    <th className="text-left px-5 py-3">Description</th>
                                    <th className="text-left px-5 py-3">Counterparty</th>
                                    <th className="text-right px-5 py-3">In</th>
                                    <th className="text-right px-5 py-3">Out</th>
                                    <th className="text-right px-5 py-3">Balance</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-sand-200">
                                {filtered.map((e) => {
                                    const Icon = SOURCE_ICON[e.source] || FileText;
                                    return (
                                        <tr key={`${e.source}-${e.id}`} className="hover:bg-sand-50/50">
                                            <td className="px-5 py-3 text-muted-foreground whitespace-nowrap">{fmtDate(e.date)}</td>
                                            <td className="px-5 py-3">
                                                <Badge className="border-0 bg-sand-100 text-ocean-700 font-normal">
                                                    <Icon className="h-3 w-3 mr-1" /> {SOURCE_LABEL[e.source] || e.source}
                                                </Badge>
                                            </td>
                                            <td className="px-5 py-3 text-ocean-700">{e.description || "—"}</td>
                                            <td className="px-5 py-3 text-muted-foreground">{e.counterparty || "—"}</td>
                                            <td className="px-5 py-3 font-mono text-right text-secondary-dark">
                                                {e.direction === "in" ? fmtMoney(e.amount, currency) : ""}
                                            </td>
                                            <td className="px-5 py-3 font-mono text-right text-coral-dark">
                                                {e.direction === "out" ? fmtMoney(e.amount, currency) : ""}
                                            </td>
                                            <td className="px-5 py-3 font-mono text-right font-medium text-ocean-700">{fmtMoney(e.balance_after, currency)}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}

function StatCard({ label, value, tone, highlight, testId }) {
    const toneBg = tone === "in" ? "bg-secondary/10 text-secondary-dark" : tone === "out" ? "bg-coral/10 text-coral-dark" : "bg-sand-100 text-ocean-700";
    return (
        <div className={`rounded-3xl border p-5 shadow-card ${highlight ? "bg-ocean-700 text-white border-ocean-700" : "bg-white border-sand-200"}`} data-testid={testId}>
            <div className={`text-xs uppercase tracking-wider font-semibold ${highlight ? "text-white/70" : "text-muted-foreground"}`}>{label}</div>
            <div className={`font-mono text-2xl mt-2 ${highlight ? "text-white" : "text-ocean-700"}`}>{value}</div>
            {tone && !highlight && <div className={`inline-flex items-center gap-1 text-xs mt-2 px-2 py-0.5 rounded-full ${toneBg}`}>{tone === "in" ? "Deposits" : "Withdrawals"}</div>}
        </div>
    );
}
